import React, { type FC, type ReactNode, useMemo } from "react";
import { ApolloClient, useApolloClient } from "@apollo/client";
import { createPaginationService, type PaginationInterpreter, type PaginationContext } from "../../common/state";
import { LenssourceModelLensSourceType as LensSourceType } from "../../../gql-generated/types";
import { LensSourcesRepository } from "../api/cache/LensSourcesRepository";

export const LensSourcesPaginationContext = React.createContext<Map<LensSourceType, PaginationInterpreter> | null>(
    null,
);

interface LensSourcesPaginationProviderProps {
    types: readonly LensSourceType[];
    children: ReactNode;
}

function loadLensSources(apolloClient: ApolloClient<any>, type: LensSourceType) {
    return (context: PaginationContext) =>
        new LensSourcesRepository(apolloClient)
            .loadLensesToCache({
                limit: context.limit,
                lensSourceType: type,
            })
            .then((data) => ({
                isLastPage: !data?.hasMore,
            }));
}

export const LensSourcesPaginationProvider: FC<LensSourcesPaginationProviderProps> = ({ children, types }) => {
    const client = useApolloClient();

    const paginationServices = useMemo(
        () =>
            new Map(types.map((type) => [type, createPaginationService(loadLensSources(client, type), { limit: 20 })])),
        [client, types],
    );

    return (
        <LensSourcesPaginationContext.Provider value={paginationServices}>
            {children}
        </LensSourcesPaginationContext.Provider>
    );
};
